import React from 'react';
import {
  Info,
  AlertTriangle,
  XCircle,
  Bug,
  FileText,
  Table2,
  ChevronRight,
  ChevronDown,
  Copy,
  ExternalLink,
} from 'lucide-react';
import { ConsoleMessage as ConsoleMessageType, MESSAGE_TYPE_CONFIG } from '../../../core/types/console.types';
import { useConsoleStore } from '../../store/consoleStore';
import { ConsoleStackTrace } from './ConsoleStackTrace';
import { cn } from '../../utils/cn';

interface Props {
  message: ConsoleMessageType;
  onNavigateToCode?: (fileName: string, lineNumber: number, columnNumber: number) => void;
}

const MessageIcon: React.FC<{ type: ConsoleMessageType['type']; size?: number }> = ({ type, size = 14 }) => {
  switch (type) {
    case 'info':
      return <Info size={size} className="text-gray-400" />;
    case 'warning':
      return <AlertTriangle size={size} className="text-yellow-500" />;
    case 'error':
      return <XCircle size={size} className="text-red-500" />;
    case 'debug':
      return <Bug size={size} className="text-purple-400" />;
    case 'log':
      return <FileText size={size} className="text-zinc-400" />;
    case 'table':
      return <Table2 size={size} className="text-blue-400" />;
    case 'group':
    case 'groupEnd':
      return <ChevronRight size={size} className="text-green-400" />;
    default:
      return <FileText size={size} className="text-zinc-400" />;
  }
};

export const ConsoleMessage: React.FC<Props> = ({ message, onNavigateToCode }) => {
  const settings = useConsoleStore((state) => state.settings);
  const selectedMessageId = useConsoleStore((state) => state.selectedMessageId);
  const selectMessage = useConsoleStore((state) => state.selectMessage);
  const copyMessage = useConsoleStore((state) => state.copyMessage);
  const toggleMessageExpanded = useConsoleStore((state) => state.toggleMessageExpanded);

  const config = MESSAGE_TYPE_CONFIG[message.type];
  const hasStackTrace = message.stackTrace && message.stackTrace.length > 0;
  const isSelected = selectedMessageId === message.id;

  const handleClick = () => {
    selectMessage(isSelected ? null : message.id);
  };

  const handleDoubleClick = () => {
    copyMessage(message.id);
  };

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    copyMessage(message.id);
  };

  const handleNavigate = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (message.fileReference && onNavigateToCode) {
      onNavigateToCode(
        message.fileReference.fileName,
        message.fileReference.lineNumber,
        message.fileReference.columnNumber
      );
    }
  };

  const formatTimestamp = (timestamp: number): string => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  return (
    <div
      data-message-id={message.id}
      className={cn(
        'group flex items-start gap-2 px-3 py-1.5 border-b border-zinc-800 cursor-pointer transition-colors',
        isSelected ? 'bg-blue-900/30' : 'hover:bg-zinc-800/50',
        message.type === 'error' && !isSelected && 'bg-red-900/10',
        message.type === 'warning' && !isSelected && 'bg-yellow-900/10'
      )}
      style={{
        paddingLeft: `${12 + message.groupLevel * 16}px`,
      }}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
    >
      {/* Expand/Collapse for stack traces */}
      {hasStackTrace && settings.showStackTraces ? (
        <button
          onClick={(e) => {
            e.stopPropagation();
            toggleMessageExpanded(message.id);
          }}
          className="p-0.5 hover:bg-zinc-700 rounded transition-colors flex-shrink-0"
        >
          {message.isExpanded ? (
            <ChevronDown size={12} className="text-zinc-400" />
          ) : (
            <ChevronRight size={12} className="text-zinc-400" />
          )}
        </button>
      ) : (
        <div className="w-4 flex-shrink-0" />
      )}

      {/* Message icon */}
      <div className="flex-shrink-0 mt-0.5">
        <MessageIcon type={message.type} />
      </div>

      {/* Timestamp */}
      {settings.showTimestamps && (
        <span className="text-xs text-zinc-500 font-mono flex-shrink-0">
          {formatTimestamp(message.timestamp)}
        </span>
      )}

      {/* Message content */}
      <div className="flex-1 min-w-0">
        <div
          className={cn(
            'text-sm font-mono break-words',
            settings.wrapLines ? 'whitespace-pre-wrap' : 'whitespace-nowrap overflow-x-auto'
          )}
          style={{ color: config.color, fontSize: `${settings.fontSize}px` }}
        >
          {message.message}
        </div>

        {/* Stack trace (expanded) */}
        {hasStackTrace && settings.showStackTraces && message.isExpanded && (
          <ConsoleStackTrace
            frames={message.stackTrace!}
            onNavigate={onNavigateToCode}
          />
        )}
      </div>

      {/* Count badge for collapsed identical messages */}
      {message.count > 1 && (
        <span className="flex-shrink-0 px-1.5 py-0.5 text-xs bg-zinc-700 text-zinc-300 rounded-full">
          {message.count}
        </span>
      )}

      {/* File reference link */}
      {message.fileReference && settings.showSource && (
        <button
          onClick={handleNavigate}
          className="flex-shrink-0 text-xs text-blue-400 hover:text-blue-300 hover:underline opacity-0 group-hover:opacity-100 transition-opacity"
        >
          {message.fileReference.fileName}:{message.fileReference.lineNumber}
        </button>
      )}

      {/* Action buttons (visible on hover) */}
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
        <button
          onClick={handleCopy}
          className="p-1 hover:bg-zinc-700 rounded text-zinc-400 hover:text-zinc-200"
          title="Copy message"
        >
          <Copy size={12} />
        </button>
        {message.fileReference && onNavigateToCode && (
          <button
            onClick={handleNavigate}
            className="p-1 hover:bg-zinc-700 rounded text-zinc-400 hover:text-zinc-200"
            title="Go to source"
          >
            <ExternalLink size={12} />
          </button>
        )}
      </div>
    </div>
  );
};
